import { supabase } from '../lib/supabase';
import toastService from '../components/ui/Toast';

// Helper to convert snake_case to camelCase
function toCamelCase(obj) {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toCamelCase);
  
  return Object.keys(obj)?.reduce((acc, key) => {
    const camelKey = key?.replace(/_([a-z])/g, (_, letter) => letter?.toUpperCase());
    acc[camelKey] = toCamelCase(obj?.[key]);
    return acc;
  }, {});
}

// Helper to convert camelCase to snake_case
function toSnakeCase(obj) {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toSnakeCase);
  
  return Object.keys(obj)?.reduce((acc, key) => {
    const snakeKey = key?.replace(/[A-Z]/g, letter => `_${letter?.toLowerCase()}`);
    acc[snakeKey] = toSnakeCase(obj?.[key]);
    return acc;
  }, {});
}

export const platformConfigService = {
  // Get platform rules configuration
  async getPlatformRules() {
    try {
      const { data, error } = await supabase
        ?.from('platform_rules')
        ?.select('*')
        ?.single();

      if (error) {
        console.error('Get platform rules error:', error);
        throw new Error(error?.message || 'Failed to fetch platform rules');
      }

      return toCamelCase(data);
    } catch (error) {
      console.error('Get platform rules error:', error);
      throw error;
    }
  },

  // Update platform rules
  async updatePlatformRules(rules) {
    try {
      const snakeCaseRules = toSnakeCase(rules);
      snakeCaseRules.updated_at = new Date()?.toISOString();

      const { data, error } = await supabase
        ?.from('platform_rules')
        ?.update(snakeCaseRules)
        ?.eq('id', '00000000-0000-0000-0000-000000000001')
        ?.select()
        ?.single();

      if (error) {
        console.error('Update platform rules error:', error);
        throw new Error(error?.message || 'Failed to update platform rules');
      }

      toastService?.success('Platform rules updated successfully');
      return toCamelCase(data);
    } catch (error) {
      console.error('Update platform rules error:', error);
      toastService?.error('Failed to update platform rules');
      throw error;
    }
  },

  // Get fee structure configuration
  async getFeeStructure() {
    try {
      const { data, error } = await supabase
        ?.from('fee_structure')
        ?.select('*')
        ?.single();

      if (error) {
        console.error('Get fee structure error:', error);
        throw new Error(error?.message || 'Failed to fetch fee structure');
      }

      return toCamelCase(data);
    } catch (error) {
      console.error('Get fee structure error:', error);
      throw error;
    }
  },

  // Update fee structure
  async updateFeeStructure(fees) {
    try {
      const snakeCaseFees = toSnakeCase(fees);
      snakeCaseFees.updated_at = new Date()?.toISOString();

      const { data, error } = await supabase
        ?.from('fee_structure')
        ?.update(snakeCaseFees)
        ?.eq('id', '00000000-0000-0000-0000-000000000001')
        ?.select()
        ?.single();

      if (error) {
        console.error('Update fee structure error:', error);
        throw new Error(error?.message || 'Failed to update fee structure');
      }

      toastService?.success('Fee structure updated successfully');
      return toCamelCase(data);
    } catch (error) {
      console.error('Update fee structure error:', error);
      toastService?.error('Failed to update fee structure');
      throw error;
    }
  },

  // Get verification requirements
  async getVerificationRequirements() {
    try {
      const { data, error } = await supabase
        ?.from('verification_requirements')
        ?.select('*')
        ?.single();

      if (error) {
        console.error('Get verification requirements error:', error);
        throw new Error(error?.message || 'Failed to fetch verification requirements');
      }

      return toCamelCase(data);
    } catch (error) {
      console.error('Get verification requirements error:', error);
      throw error;
    }
  },

  // Update verification requirements
  async updateVerificationRequirements(requirements) {
    try {
      const snakeCaseRequirements = toSnakeCase(requirements);
      snakeCaseRequirements.updated_at = new Date()?.toISOString();

      const { data, error } = await supabase
        ?.from('verification_requirements')
        ?.update(snakeCaseRequirements)
        ?.eq('id', '00000000-0000-0000-0000-000000000001')
        ?.select()
        ?.single();

      if (error) {
        console.error('Update verification requirements error:', error);
        throw new Error(error?.message || 'Failed to update verification requirements');
      }

      toastService?.success('Verification requirements updated successfully');
      return toCamelCase(data);
    } catch (error) {
      console.error('Update verification requirements error:', error);
      toastService?.error('Failed to update verification requirements');
      throw error;
    }
  },

  // Get policy templates
  async getPolicyTemplates() {
    try {
      const { data, error } = await supabase
        ?.from('policy_templates')
        ?.select('*')
        ?.order('created_at', { ascending: false });

      if (error) {
        console.error('Get policy templates error:', error);
        throw new Error(error?.message || 'Failed to fetch policy templates');
      }

      return data ? data?.map(toCamelCase) : [];
    } catch (error) {
      console.error('Get policy templates error:', error);
      throw error;
    }
  },

  // Create policy template
  async createPolicyTemplate(template) {
    try {
      const snakeCaseTemplate = toSnakeCase(template);

      const { data, error } = await supabase
        ?.from('policy_templates')
        ?.insert(snakeCaseTemplate)
        ?.select()
        ?.single();

      if (error) {
        console.error('Create policy template error:', error);
        throw new Error(error?.message || 'Failed to create policy template');
      }

      toastService?.success('Policy template created successfully');
      return toCamelCase(data);
    } catch (error) {
      console.error('Create policy template error:', error);
      toastService?.error('Failed to create policy template');
      throw error;
    }
  },

  // Update policy template
  async updatePolicyTemplate(id, template) {
    try {
      const snakeCaseTemplate = toSnakeCase(template);
      snakeCaseTemplate.updated_at = new Date()?.toISOString();

      const { data, error } = await supabase
        ?.from('policy_templates')
        ?.update(snakeCaseTemplate)
        ?.eq('id', id)
        ?.select()
        ?.single();

      if (error) {
        console.error('Update policy template error:', error);
        throw new Error(error?.message || 'Failed to update policy template');
      }

      toastService?.success('Policy template updated successfully');
      return toCamelCase(data);
    } catch (error) {
      console.error('Update policy template error:', error);
      toastService?.error('Failed to update policy template');
      throw error;
    }
  },

  // Get audit trail
  async getAuditTrail(tableName = null, limit = 50) {
    try {
      let query = supabase
        ?.from('config_audit_trail')
        ?.select('*')
        ?.order('changed_at', { ascending: false })
        ?.limit(limit);

      if (tableName) {
        query = query?.eq('table_name', tableName);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Get audit trail error:', error);
        throw new Error(error?.message || 'Failed to fetch audit trail');
      }

      return data ? data?.map(toCamelCase) : [];
    } catch (error) {
      console.error('Get audit trail error:', error);
      throw error;
    }
  },

  // Rollback configuration to previous version
  async rollbackConfiguration(tableName, auditId) {
    try {
      // Get the audit record
      const { data: auditRecord, error: auditError } = await supabase
        ?.from('config_audit_trail')
        ?.select('*')
        ?.eq('id', auditId)
        ?.single();

      if (auditError) {
        throw new Error('Failed to fetch audit record');
      }

      // Restore old values
      const oldValues = auditRecord?.old_values;
      if (!oldValues) {
        throw new Error('No previous version available');
      }

      // Update the configuration table
      const { data, error } = await supabase
        ?.from(tableName)
        ?.update(oldValues)
        ?.eq('id', auditRecord?.record_id)
        ?.select()
        ?.single();

      if (error) {
        throw new Error('Failed to rollback configuration');
      }

      toastService?.success('Configuration rolled back successfully');
      return toCamelCase(data);
    } catch (error) {
      console.error('Rollback configuration error:', error);
      toastService?.error('Failed to rollback configuration');
      throw error;
    }
  },

  // Subscribe to configuration changes
  subscribeToConfigChanges(tableName, callback) {
    const channel = supabase
      ?.channel(`config-changes-${tableName}`)
      ?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: tableName
        },
        (payload) => {
          callback(toCamelCase(payload?.new));
        }
      )
      ?.subscribe();

    return () => {
      supabase?.removeChannel(channel);
    };
  }
};

export default platformConfigService;